import firebase from 'firebase/app'

import * as crud from './crud'
import { assert } from '../helpers'
import { getBalancesByUser, createBalance } from './balances'


const userMapping = async (doc, data) => {
  const wallets = await getBalancesByUser(doc.id, {
    onlyItems: true,
  })

  return {
    ...crud.getDefaultMapping(doc, data),
    wallets,
    updated_at: data.updated_at && data.updated_at.toDate(),
    created_at: data.created_at && data.created_at.toDate(),
  }
}

const walletMapping = async (doc, data) => {
  return {
    ...crud.getDefaultMapping(doc, data),
    updated_at: data.updated_at && data.updated_at.toDate(),
    created_at: data.created_at && data.created_at.toDate(),
    amount_expired_at: data.amount_expired_at ? data.amount_expired_at.toDate() : new Date(),
  }
}


/**
 * Get list of users
 * @param {*} options - Options
 * @returns List of users
 */
export const getUsers = async (options = {}) => {
  const db = firebase.firestore()

  return crud.getAll(db.collection('users'), {
    ...options,
    map: userMapping,
  })
}


/**
 * Get wallets of a user
 * @param {*} userId - The id of the user
 * @param {*} options - Options of the request
 * @returns {Promise}
 */
export const getUserWallets = async (userId, options = {}) => {
  const db = firebase.firestore()

  return crud.getAll(db.collection('users').doc(userId).collection('wallets'), options)
}

/**
 * Expire a wallet of a user
 * @param {*} userId - The id of the user
 * @param {*} walletId - The id of the wallet
 * @returns {Promise}
 */
export const expireUserWallet = async (userId, walletId) => {
  const expires_at = new Date()

  expires_at.setMinutes(expires_at.getMinutes() - 1)

  await firebase.functions().httpsCallable('triggerExpireWallet')({
    userId,
    walletId,
  })
}

/**
 * Get users with all expired wallets
 * @param {*} options - Options
 * @returns List of expired users
 */
export const getExpiredUsers = async (options = {}) => {
  const db = firebase.firestore()

  const snapshot = (!options.orderBy || (options.orderBy && options.orderBy !== 'last_wallet_expires_at'))
    ? db.collection('users').where('last_wallet_expires_at', '<=', new Date()).orderBy('last_wallet_expires_at', 'desc')
    : db.collection('users').where('last_wallet_expires_at', '<=', new Date())

  return crud.getAll(snapshot, options)
}

/**
 * Get wallets expired
 * @param {*} options - Options
 * @returns List of expired wallets
 */
export const getExpiredWallets = async (options = {}) => {
  const db = firebase.firestore()

  const snapshot = db.collectionGroup('wallets')
    .where('amount_expired_at', '<', new Date())

  return crud.getAll(snapshot, {
    ...options,
    map: walletMapping,
    orderBy: 'amount_expired_at',
  })
}

/**
 * Get a user by its id
 * @param {*} uid - The id of the user
 * @returns {User} The user
 */
export const getUser = async (uid) => {
  const db = firebase.firestore()
  const snapshot = await db.collection('users').doc(uid).get()

  return userMapping(snapshot, snapshot.data())
}


/**
 * Get a user by its phone number
 * @param {String} phone - The phone number
 * @returns {User} The user
 */
export const getUserByPhone = async (phone, options) => {
  const db = firebase.firestore()

  return crud.getAll(db.collection('users').where('phone', '==', phone), {
    ...options,
    first: true,
    map: userMapping,
  })
}


/**
 * Get a user by its email
 * @param {String} email - The email
 * @returns {User} The user
 */
export const getUserByEmail = async (email, options = {}) => {
  const db = firebase.firestore()

  return crud.getAll(db.collection('users').where('email', '==', email), {
    ...options,
    first: true,
    map: userMapping,
  })
}

export const checkUserEmail = async (email, options = {}) => {
  const userWithEmail = await getUserByEmail(email, options)

  assert(!userWithEmail, `Un utilisateuer avec l'email "${email}" existe déjà.`)
}

export const checkUserPhone = async (phone, options = {}) => {
  const userWithPhone = await getUserByPhone(phone, options)

  assert(!userWithPhone, `Un utilisateur avec le numéro "${phone}" existe déjà.`)
}


/**
 * Edit attribute of a user object
 * @param {String} userId - The id of the user
 * @param {Values} values - List of editable fields
 * @returns {Promise}
 */
export const editUser = async (userId, values = {}) => {
  const db = firebase.firestore()
  const promises = []
  const user = await getUser(userId)

  if (values.phone && values.phone !== user.phone) {
    promises.push(checkUserPhone(values.phone))
  }

  if (values.email && values.email !== user.email) {
    promises.push(checkUserEmail(values.email))
  }

  if (promises.length > 0) {
    await Promise.all(promises)
  }

  return crud.edit(db.collection('users').doc(userId), values, {
    map: userMapping,
  })
}

export const lockTransaction = userId => editUser(userId, { transaction_available: false })

export const unlockTransaction = userId => editUser(userId, { transaction_available: true })


export const getDefaultData = (data = {}) => ({
  ...data,
  created_at: new Date(),
  total_balance: data.balance || 0,
})


/**
 * Add a new User
 * @param {ShopData} data - The data of  the user
 * @returns {Shop} The user object
 */
export const addUser = async (data, options = {}) => {
  const { phone, email, balance } = data
  const db = firebase.firestore()

  assert(email, 'Un email est obligatoire.')
  assert(!isNaN(balance), 'Le montant est invalide')
  assert(phone, 'Le numéro de téléphone est obligatoire.')

  await Promise.all([
    checkUserPhone(phone, options),
    checkUserEmail(phone, options),
  ])

  return crud.add(db.collection('users'), data, {
    onCreate: snap => createBalance(snap.id, {
      initial_amount: data.balance,
      available_amount: data.balance,
      expired: false,
    }),
  })
}
